import { cn, getCurrencyFormat, useBudgetCurrency, useBudgetLocale } from '@liquidbudget/common';
import { useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';

interface BaseCurrencyInputProps {
  value?: number | null;
  className?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  allowNegativeValue?: boolean;
  onChange?: (value: string | null) => void; // Returns numeric value as a string
}

const defaultInputStyles = `
  flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm
  shadow-sm transition-colors placeholder:text-muted-foreground
  focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
  disabled:cursor-not-allowed disabled:opacity-50
`;

export function BaseCurrencyInput({
  value,
  required = false,
  disabled = false,
  onChange,
  placeholder,
  allowNegativeValue = false,
  className,
}: BaseCurrencyInputProps) {
  const locale = useBudgetLocale();
  const currency = useBudgetCurrency();

  // Get formatting details from the same function used in mobile
  const { delimiter, separator, symbol, isPrefix } = getCurrencyFormat(locale, currency);

  // State to store the formatted currency display value
  const [formattedValue, setFormattedValue] = useState<string>('');

  // Initialize formatted value when backend value changes
  useEffect(() => {
    setFormattedValue(value && value !== 0 ? value.toString() : '');
  }, [value]);

  return (
    <CurrencyInput
      value={formattedValue}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      allowNegativeValue={allowNegativeValue}
      className={cn(defaultInputStyles, className)}
      prefix={isPrefix ? symbol : undefined}
      suffix={!isPrefix ? symbol : undefined}
      groupSeparator={delimiter}
      decimalSeparator={separator}
      decimalsLimit={2}
      decimalScale={2}
      onValueChange={(formatted, _, values) => {
        setFormattedValue(formatted || '');
        onChange?.(values?.float !== null ? String(values?.float) : null);
      }}
    />
  );
}
